import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import LayoutDefault from '../layouts/LayoutDefault';
import RouteSeo from '../../../seo/RouteSeo';
import { MARKETING_PAGE_META } from '../../../data/marketingPageMeta';
import '../assets/scss/style.scss';
import { COMPETITOR_ICONS } from '../data/competitorIcons';

// Light-page palette for each competitor's icon chip.
const ICON_STYLE = {
    'google-keep': { color: '#b8860b', background: 'rgba(255, 187, 0, 0.15)' },
    'apple-notes': { color: '#14110f', background: 'rgba(20, 17, 15, 0.08)' },
    pocket: { color: '#EF3F56', background: 'rgba(239, 63, 86, 0.12)' },
    evernote: { color: '#00A82D', background: 'rgba(0, 168, 45, 0.12)' },
    fabric: { background: 'rgba(20, 17, 15, 0.06)' },
    mymind: { background: 'rgba(20, 17, 15, 0.06)' },
    glasp: { background: 'rgba(20, 17, 15, 0.06)' },
    notion: { color: '#14110f', background: 'rgba(20, 17, 15, 0.08)' },
    obsidian: { color: '#7c3aed', background: 'rgba(124, 58, 237, 0.14)' },
    dropbox: { color: '#0061ff', background: 'rgba(0, 97, 255, 0.14)' },
    'google-drive': { color: '#1a73e8', background: 'rgba(26, 115, 232, 0.14)' },
    pinterest: { color: '#e60023', background: 'rgba(230, 0, 35, 0.12)' },
    raindrop: { color: '#3a97d3', background: 'rgba(58, 151, 211, 0.14)' },
    otter: { color: '#00918f', background: 'rgba(0, 178, 178, 0.14)' },
    voicenotes: { color: '#f97316', background: 'rgba(249, 115, 22, 0.14)' }
};

const ICON_MAP = Object.fromEntries(
    Object.entries(COMPETITOR_ICONS).map(([key, icon]) => [key, { ...icon, ...ICON_STYLE[key] }])
);

// Grouped by the axis a searcher is actually comparing against (see
// docs/USE_CASES_AND_FOR_PAGES_PLAN.md, "/compare hub" section).
const COMPARISON_CATEGORIES = [
    {
        label: 'Notes apps',
        items: [
            {
                path: '/compare/notion-alternative',
                key: 'notion',
                title: 'Notion',
                description: 'Fast capture vs. workspace building, AI search, pricing, and who each tool fits.'
            },
            {
                path: '/compare/evernote-alternative',
                key: 'evernote',
                title: 'Evernote',
                description: 'Chat-style capture, AI search, notebooks, tasks, migration, and pricing.'
            },
            {
                path: '/compare/obsidian-alternative',
                key: 'obsidian',
                title: 'Obsidian',
                description: 'Zero setup vs. a local-first vault, sync, AI search, and mobile capture.'
            },
            {
                path: '/compare/apple-notes-alternative',
                key: 'apple-notes',
                title: 'Apple Notes',
                description: 'Sync across devices, AI search, web capture, handwriting, and more.'
            },
            {
                path: '/compare/google-keep-alternative',
                key: 'google-keep',
                title: 'Google Keep',
                description: 'Capture speed, web saving, AI search, organization, tasks, and pricing.'
            }
        ]
    },
    {
        label: 'Bookmarking & AI save',
        items: [
            {
                path: '/compare/raindrop-alternative',
                key: 'raindrop',
                title: 'Raindrop',
                description: 'Bookmark management vs. one inbox for links, notes, voice, tasks, and AI search.'
            },
            {
                path: '/compare/pocket-alternatives',
                key: 'pocket',
                title: 'Pocket',
                description: 'Save from web, read later, archive, tagging, and retrieval without the noise.'
            },
            {
                path: '/compare/mymind-alternative',
                key: 'mymind',
                title: 'Mymind',
                description: 'AI-powered curation, visual search, infinite canvas, and retrieval.'
            },
            {
                path: '/compare/glasp-alternative',
                key: 'glasp',
                title: 'Glasp',
                description: 'Private everyday capture vs. social web highlighting, research summaries, and exports.'
            },
            {
                path: '/compare/fabric-alternative',
                key: 'fabric',
                title: 'Fabric',
                description: 'Personal knowledge base, web clipper, AI features, and organization.'
            }
        ]
    },
    {
        label: 'Cloud storage',
        items: [
            {
                path: '/compare/dropbox-alternative',
                key: 'dropbox',
                title: 'Dropbox',
                description: 'Second-brain capture and search vs. synced folders, storage, and file sharing.'
            },
            {
                path: '/compare/google-drive-alternative',
                key: 'google-drive',
                title: 'Google Drive',
                description: 'Quick capture and AI retrieval vs. cloud storage, Docs, Sheets, and collaboration.'
            }
        ]
    },
    {
        label: 'Voice notes',
        items: [
            {
                path: '/compare/otter-alternative',
                key: 'otter',
                title: 'Otter',
                description: 'Personal voice capture vs. meeting transcription, speaker labels, and integrations.'
            },
            {
                path: '/compare/voicenotes-alternative',
                key: 'voicenotes',
                title: 'VoiceNotes',
                description: 'Mixed capture and AI search vs. voice-first transcription and content drafts.'
            }
        ]
    },
    {
        label: 'Visual discovery',
        items: [
            {
                path: '/compare/pinterest-alternative',
                key: 'pinterest',
                title: 'Pinterest',
                description: 'Private capture and retrieval vs. public visual discovery and inspiration boards.'
            }
        ]
    },
    {
        label: 'Head to head',
        items: [
            {
                path: '/compare/pocket-vs-raindrop',
                key: 'raindrop',
                title: 'Pocket vs Raindrop',
                description: 'Pocket shut down in 2025. See how Raindrop compares, plus a third option built for fast capture.'
            },
            {
                path: '/compare/notion-vs-evernote',
                key: 'notion',
                title: 'Notion vs Evernote',
                description: 'Workspace builder vs. notebook-style capture, and a third option if you just want speed.'
            },
            {
                path: '/compare/mymind-vs-fabric',
                key: 'mymind',
                title: 'mymind vs Fabric',
                description: 'Visual memory board vs. AI-organized workspace, and a third option built around chat-style capture.'
            }
        ]
    }
];

// Newest lastmod first. Missing lastmod always sorts last. Equal dates keep
// their existing relative order (Array.prototype.sort is stable), preserving
// the curated ordering within each category as a tie-break. Groups and their
// order are never touched — only the items within each group are sorted.
const byLastmodDesc = (a, b) => {
    const aLastmod = MARKETING_PAGE_META.find((candidate) => candidate.path === a.path)?.lastmod;
    const bLastmod = MARKETING_PAGE_META.find((candidate) => candidate.path === b.path)?.lastmod;
    if (!aLastmod && !bLastmod) return 0;
    if (!aLastmod) return 1;
    if (!bLastmod) return -1;
    if (aLastmod > bLastmod) return -1;
    if (aLastmod < bLastmod) return 1;
    return 0;
};

const SORTED_COMPARISON_CATEGORIES = COMPARISON_CATEGORIES.map((category) => ({
    ...category,
    items: [...category.items].sort(byLastmodDesc)
}));

const ComparePage = () => {
    return (
        <>
            <RouteSeo
                title="Compare Luckynote to Other Note-Taking Apps"
                description="See how Luckynote compares to Google Keep, Apple Notes, Pocket, Evernote, Fabric, and Mymind. Find the right place to send everything you don't want to forget."
                canonical="https://luckynote.io/compare"
            />
            <LayoutDefault>
                <DarkSection>
                    <PageShell>
                        <HeroSection>
                            <HeroTitle>Compare</HeroTitle>
                            <HeroDescription>
                                See how Luckynote stacks up against other apps for saving and remembering things.
                            </HeroDescription>
                        </HeroSection>

                        {SORTED_COMPARISON_CATEGORIES.map((category) => (
                            <CategorySection key={category.label}>
                                <CategoryLabel>{category.label}</CategoryLabel>
                                <CardGrid>
                                    {category.items.map((comparison) => {
                                        const icon = ICON_MAP[comparison.key];
                                        const IconComponent = icon.component;
                                        return (
                                            <CompareCard key={comparison.path} to={comparison.path}>
                                                <IconBadge $color={icon.color} $background={icon.background}>
                                                    {IconComponent ? (
                                                        <IconComponent size={22} />
                                                    ) : icon.image ? (
                                                        <img src={icon.image} alt="" width={26} height={26} />
                                                    ) : (
                                                        icon.letter
                                                    )}
                                                </IconBadge>
                                                <CardTitle>{comparison.title}</CardTitle>
                                                <CardBody>{comparison.description}</CardBody>
                                                <CardCta>
                                                    Compare
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </CardCta>
                                            </CompareCard>
                                        );
                                    })}
                                </CardGrid>
                            </CategorySection>
                        ))}

                        <FooterNote>
                            Looking for a workflow instead? <Link to="/use-cases">Browse use cases</Link>, or find
                            your persona on the <Link to="/for">who it&apos;s for</Link> page.
                        </FooterNote>
                    </PageShell>
                </DarkSection>
            </LayoutDefault>
        </>
    );
};

export default ComparePage;

const PAPER = '#fcf9f7';
const PAPER_DEEP = '#f6efe7';

const DarkSection = styled.div`
    background:
        radial-gradient(circle at 14% 4%, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0) 26rem),
        linear-gradient(180deg, ${PAPER} 0%, ${PAPER_DEEP} 56%, ${PAPER} 100%);
    min-height: 100vh;
`;

const PageShell = styled.div`
    max-width: 1120px;
    margin: 0 auto;
    padding: 96px 20px 96px;
`;

const HeroSection = styled.section`
    padding: 0 0 56px;
    text-align: center;
`;

const HeroTitle = styled.h1`
    margin: 0 0 16px;
    font-size: clamp(2rem, 4vw, 3.2rem);
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-weight: 700;
    color: #14110f;
`;

const HeroDescription = styled.p`
    margin: 0 auto;
    font-size: 1.1rem;
    line-height: 1.7;
    color: rgba(20, 17, 15, 0.66);
    max-width: 38rem;
`;

const CategorySection = styled.section`
    margin: 0 0 44px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const CategoryLabel = styled.h2`
    margin: 0 0 18px;
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(20, 17, 15, 0.48);
`;

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
`;

const CompareCard = styled(Link)`
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 28px 26px;
    border-radius: 14px;
    border: 1px solid rgba(20, 17, 15, 0.12);
    background: rgba(255, 255, 255, 0.58);
    text-decoration: none;
    backdrop-filter: blur(12px);
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.88);
        border-color: rgba(20, 17, 15, 0.22);
        box-shadow: 0 18px 42px rgba(59, 46, 35, 0.16);
        transform: translateY(-3px);
    }
`;

const IconBadge = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ $background }) => $background};
    color: ${({ $color }) => $color};
    font-weight: 700;
    font-size: 1.1rem;
    overflow: hidden;
    position: relative;

    img {
        border-radius: 4px;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
`;

const CardTitle = styled.h3`
    margin: 0;
    font-size: 1.15rem;
    color: #14110f;
`;

const CardBody = styled.p`
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.65;
    color: rgba(20, 17, 15, 0.62);
    flex-grow: 1;
`;

const CardCta = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.9rem;
    font-weight: 600;
    color: #2b88d9;

    svg {
        transition: transform 0.15s ease;
    }

    ${CompareCard}:hover & svg {
        transform: translateX(3px);
    }
`;

const FooterNote = styled.p`
    margin: 48px auto 0;
    text-align: center;
    font-size: 0.95rem;
    color: rgba(20, 17, 15, 0.62);

    a {
        color: #2b88d9;
        font-weight: 600;
    }
`;
